/**
 * ONE decision rule for "did that gesture change tables?", shared by touch and
 * trackpad so the two feel identical by construction.
 *
 * ## Momentum projection, not two OR'd thresholds
 *
 * The gesture used to commit on `|dx| >= 25% of viewport` OR a `45px / 300ms`
 * flick. Two rules with a discontinuity between them: a slow 24% drag springs
 * back, a lazy 46px twitch commits. Players feel that as "oversensitive
 * sometimes, unresponsive other times" — it is the same complaint twice.
 *
 * Instead, project where the content WOULD land if it kept moving under
 * inertia, then snap from there — the rule iOS itself uses (WWDC 2018 "Designing
 * Fluid Interfaces", session 803; the same projection UIScrollView applies via
 * its `decelerationRate`):
 *
 *     projected = offset + velocity * d / (1 - d)
 *
 * `d` is the per-millisecond deceleration rate, so velocity is px/ms.
 * UIScrollView's `.normal` is 0.998 (×499 multiplier) and `.fast` is 0.99
 * (×99). We use `.normal`: a gentle 0.2px/ms flick projects ~100px, which on a
 * 390px-wide phone clears half a screen when added to what the finger already
 * carried, and a slow drag projects nearly nothing and must therefore cross the
 * halfway line on distance alone. Distance and speed stop being separate rules
 * and become one continuous quantity.
 *
 * The result is clamped to ±1 table: a hard flick advances exactly one table
 * rather than skipping three (Android ViewPager2's rule, and the behaviour
 * players expect from a pager).
 */

/** UIScrollView `.normal` deceleration, per millisecond. */
export const DECELERATION_RATE = 0.998;

/** Travel before a gesture counts as a horizontal drag rather than a tap. */
export const DRAG_START_PX = 8;

/**
 * How far the projected landing must cross toward the neighbour to commit.
 * Half a table: the projection already folds speed in, so the bar itself is
 * the plain "past the midpoint" rule rather than a tuned fudge factor.
 */
export const COMMIT_FRACTION = 0.5;

/**
 * How far past the first/last table the rubber band may EVER stretch, as a
 * fraction of the viewport. The band is asymptotic (see applyEdgeResistance),
 * so this is a true ceiling, not a threshold.
 *
 * 8% is deliberately tight. This is a poker table, not a photo gallery: the gap
 * behind the felt is dead black space, and showing a third of the screen of it
 * — which is what the previous unbounded curve did on a trackpad flick — reads
 * as the UI having come loose rather than as a boundary.
 */
export const EDGE_MAX_STRETCH_FRACTION = 0.08;

/** Where an inertial fling would come to rest, given current px/ms velocity. */
export const projectMomentum = (
  velocityPxPerMs: number,
  decelerationRate: number = DECELERATION_RATE
): number => (velocityPxPerMs * decelerationRate) / (1 - decelerationRate);

/**
 * Resisted offset when there is no table in the dragged direction — an
 * ASYMPTOTIC rubber band that approaches `maxPx` and never reaches it.
 *
 *     f(x) = maxPx · x / (x + maxPx)
 *
 * Responsive at the start (half the cap is reached at x = maxPx), immovable at
 * the end, and bounded for any input at all. That last property is the fix: the
 * previous `pow(x, 0.7)` had no ceiling, and while a FINGER can only travel a
 * screen-width, a trackpad's momentum tail keeps delivering deltas for a second
 * or more after the fingers lift. Accumulating those pushed x into the
 * thousands, and pow(4000, 0.7) ≈ 332px — a third of the viewport of black
 * space behind the felt, which is exactly the "way too much, very jarring" gap
 * Jason hit (2026-08-16). No amount of flicking can produce that now.
 */
export const applyEdgeResistance = (dx: number, viewportPx = 1000): number => {
  const maxPx = Math.max(1, viewportPx) * EDGE_MAX_STRETCH_FRACTION;
  const x = Math.abs(dx);
  return Math.sign(dx) * ((maxPx * x) / (x + maxPx));
};

export type SwipeDecision = {
  /** -1 = previous table, +1 = next table, 0 = spring back to current. */
  direction: -1 | 0 | 1;
  /** Projected landing offset in px — drives the settle duration. */
  projectedPx: number;
};

/**
 * Decide the outcome of a released gesture.
 *
 * @param offsetPx     how far the track has been carried (negative = toward next)
 * @param velocityPxPerMs release velocity, same sign convention as offsetPx
 * @param viewportPx   width of one table
 * @param hasPrev/hasNext whether a table exists that way (edges never commit)
 */
export const decideSwipe = (
  offsetPx: number,
  velocityPxPerMs: number,
  viewportPx: number,
  hasPrev: boolean,
  hasNext: boolean
): SwipeDecision => {
  const projectedPx = offsetPx + projectMomentum(velocityPxPerMs);
  const threshold = Math.max(1, viewportPx) * COMMIT_FRACTION;

  // Clamp to a single table in either direction, and never commit past an edge.
  if (projectedPx <= -threshold && hasNext) return { direction: 1, projectedPx };
  if (projectedPx >= threshold && hasPrev) return { direction: -1, projectedPx };
  return { direction: 0, projectedPx };
};

/** Settle timing bounds — fast enough to feel immediate, slow enough to read. */
export const SETTLE_MIN_MS = 160;
export const SETTLE_MAX_MS = 420;
/** Fallback when there is no meaningful release velocity (a slow drag). */
export const SETTLE_DEFAULT_MS = 260;
/**
 * A gesture that did NOT commit always returns at this one speed, whatever the
 * release velocity — see settleDurationMs. Slightly quicker than a committed
 * settle: the system is answering, not continuing.
 */
export const SETTLE_SPRING_BACK_MS = 220;

/**
 * How long the settle should take, given how far it still has to travel and
 * how fast the gesture was moving when released.
 *
 * The old settle was a FIXED 260ms no matter what: a hard flick and a lazy
 * drag-and-let-go finished at the same speed, so the animation always felt
 * like it overrode the gesture rather than continuing it. That is the
 * "abrupt" — motion that ignores your hand. Carrying release velocity into the
 * settle is what makes a flick feel like it flew and a nudge feel like it
 * eased. Clamped at both ends so a violent flick still animates (rather than
 * teleporting) and a near-stationary release doesn't crawl.
 */
export const settleDurationMs = (
  remainingPx: number,
  velocityPxPerMs: number,
  committed = true
): number => {
  // A SPRING-BACK is not a continuation of the gesture — it is the system
  // saying "no". Carrying release velocity into it produced the exact glitch
  // Jason reported at the edges (2026-08-16): the band stretched slowly under
  // a trackpad's momentum, then snapped back in the 160ms floor because that
  // same momentum was still reported as high velocity. Stretch slow, return
  // fast, every time — which reads as a hiccup rather than a bounce. A failed
  // gesture always returns at one calm, predictable speed instead.
  if (!committed) return SETTLE_SPRING_BACK_MS;

  const speed = Math.abs(velocityPxPerMs);
  const distance = Math.abs(remainingPx);
  if (speed < 0.05 || distance < 1) return SETTLE_DEFAULT_MS;
  return Math.min(SETTLE_MAX_MS, Math.max(SETTLE_MIN_MS, distance / speed));
};
